import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TelegramConfigureDialog } from "@/components/connections/TelegramConfigureDialog";
import { QuoraConfigureDialog } from "@/components/connections/QuoraConfigureDialog";
import { LinkedInPairingDialog } from "@/components/connections/LinkedInPairingDialog";
import { ChannelPairingDialog } from "@/components/connections/ChannelPairingDialog";
import { WhatsAppManageDialog } from "@/components/connections/WhatsAppManageDialog";
import { ConnectionChannelCard } from "@/pages/settings/ConnectionChannelCard";
import { ConnectionStatusBadge } from "@/pages/settings/ConnectionStatusBadge";
import { CONNECTION_KINDS } from "@/pages/settings/connectionsBootstrap";
import { connectionDisplayStatus } from "@/lib/connectionStatus";
import { useAssistantName } from "@/hooks/useAssistantName";
import { useOnboardingStore } from "@/store/useOnboardingStore";
import { onboardingApi } from "@/lib/api";
import { resolveStoredOnboardingStep } from "@/lib/onboardingSteps";

export function ConnectionsSettingsView({ Shell, conn }) {
  const assistantName = useAssistantName();
  const navigate = useNavigate();
  const hydrateFromServer = useOnboardingStore((s) => s.hydrateFromServer);
  const setStep = useOnboardingStore((s) => s.setStep);
  const currentStep = useOnboardingStore((s) => s.currentStep);
  const onboardingCompleted = useOnboardingStore((s) => s.completed);

  const resumeOnboarding = async () => {
    try {
      const status = await onboardingApi.status();
      hydrateFromServer(status);
      if (status.onboarding_completed) {
        navigate("/today");
        return;
      }
      const step = resolveStoredOnboardingStep(status.onboarding_step);
      setStep(step);
      navigate(`/onboarding/${step}`);
    } catch {
      const step = Math.max(1, Number(currentStep) || 1);
      setStep(step);
      navigate(`/onboarding/${step}`);
    }
  };

  return (
    <Shell title="Connections" desc={`Where ${assistantName} can talk. OAuth pairing ships in Sprints 5 + 6.`}>
      <div className="p-4 sm:p-6 max-w-5xl space-y-6 sm:space-y-7 min-w-0" data-testid="connections-page">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-indigo-50/40 via-white to-white p-4 sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5 sm:flex-wrap min-w-0">
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold inline-flex items-center gap-1"><Sparkles size={11} /> {assistantName} is connected</div>
              <div className="mt-1 inline-flex items-baseline gap-2 flex-wrap">
                <span className="text-[32px] sm:text-[36px] font-bold tracking-tight tabular-nums text-slate-900 leading-none" data-testid="connections-connected-count">{conn.connectedCount}</span>
                <span className="text-[13px] text-slate-500">/ {conn.totalCount} channels paired</span>
              </div>
            </div>
            <div className="hidden sm:block h-10 w-px bg-slate-200 shrink-0" />
            <div className="flex items-center gap-3 sm:gap-4 flex-wrap text-[11.5px] text-slate-600">
              <div className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> {conn.connectedCount} live</div>
              <div className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-slate-300" /> {conn.totalCount - conn.connectedCount} pending</div>
              <div className="inline-flex items-center gap-1.5 text-indigo-700"><Lock size={11} /> Sessions held in your instance</div>
            </div>
            <div className="sm:ml-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              {!onboardingCompleted && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-[11.5px]"
                  data-testid="connections-start-onboarding"
                  onClick={resumeOnboarding}
                >
                  <Sparkles size={13} /> Start onboarding
                </Button>
              )}
              <div
                className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-slate-200 px-2.5 py-1.5 text-[10.5px] text-slate-500 max-w-full select-none"
                data-testid="powered-by-openclaw-pill"
                aria-hidden="true"
              >
                <span className="font-semibold text-slate-700">Powered by</span>
                <span className="font-mono text-indigo-700">OpenClaw</span>
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                <span className="truncate">gateway healthy</span>
              </div>
            </div>
          </div>
        </div>

        <section className="min-w-0">
          <h2 className="text-[15px] font-semibold tracking-tight text-slate-900">Channels</h2>
          <p className="text-[12px] text-slate-500 mt-0.5 mb-3">
            Sprint 4 ships the schema and disconnected placeholder rows. Live OAuth pairing lands sprint-by-sprint.
            {!conn.canEdit && <span className="ml-1 text-amber-700">· Your role ({conn.role}) can view but not pair.</span>}
          </p>
          {conn.loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="connections-loading">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 animate-pulse">
                  <div className="h-9 w-9 rounded-lg bg-slate-100 mb-3" />
                  <div className="h-3 w-24 bg-slate-200 rounded mb-2" />
                  <div className="h-3 w-full bg-slate-100 rounded" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {CONNECTION_KINDS.map((c) => {
                const row = conn.byKind[c.kind];
                const status = connectionDisplayStatus(row, conn.seeding, c.kind);
                return (
                  <ConnectionChannelCard
                    key={c.kind}
                    c={c}
                    row={row}
                    status={status}
                    googleOauthStatus={conn.googleOauthStatus}
                    canEdit={conn.canEdit}
                    disconnectBusy={conn.disconnectBusy}
                    pairingBusy={conn.pairingBusy}
                    telegramSyncBusy={conn.telegramSyncBusy}
                    onDisconnect={conn.handleDisconnect}
                    onAction={conn.handleChannelAction}
                    onReconfigure={conn.handleTelegramReconfigure}
                    actionTitle={conn.actionTitle}
                    StatusBadge={ConnectionStatusBadge}
                  />
                );
              })}
            </div>
          )}
        </section>
      </div>

      <TelegramConfigureDialog
        open={conn.telegramOpen}
        onOpenChange={conn.setTelegramOpen}
        connectionId={conn.byKind.telegram?.id}
        canEdit={conn.canEdit}
        onConfigured={conn.handleTelegramConfigured}
      />
      <QuoraConfigureDialog
        open={conn.quoraOpen}
        onOpenChange={conn.setQuoraOpen}
        connectionId={conn.byKind.quora?.id}
        canEdit={conn.canEdit}
        onConfigured={() => conn.handleSimpleChannelConfigured("Quora connected")}
      />
      <LinkedInPairingDialog
        open={conn.linkedinOpen}
        onOpenChange={conn.setLinkedinOpen}
        connectionId={conn.byKind.linkedin?.id}
        onConfigured={() => conn.handleSimpleChannelConfigured("LinkedIn connected")}
      />
      <ChannelPairingDialog
        open={!!conn.pairingConn}
        connection={conn.pairingConn}
        pairing={conn.pairingPayload}
        loading={conn.pairingBusy && !conn.pairingPayload}
        onOpenChange={(open) => { if (!open) conn.closePairingModal(); }}
        onRetry={conn.handlePairingRetry}
        onPaired={conn.handlePairingPaired}
      />
      <WhatsAppManageDialog
        open={conn.whatsappManageOpen}
        onOpenChange={(open) => { if (!open) conn.closeWhatsappManage(); }}
        row={conn.whatsappManageRow}
        canEdit={conn.canEdit}
        busy={conn.whatsappManageBusy}
        onPairDifferent={conn.handleWhatsappPairDifferent}
        onDisconnect={conn.handleWhatsappManageDisconnect}
      />
    </Shell>
  );
}

ConnectionsSettingsView.propTypes = {
  Shell: PropTypes.elementType.isRequired,
  conn: PropTypes.object.isRequired,
};
